"use client";
import { useEffect, useState, useCallback } from "react";
import { c } from "@/lib/vster/theme";

interface Key {
  id: number; name: string; keyPrefix: string; rateLimit: number;
  active: boolean; lastUsedAt: string | null; createdAt: string;
}

// 로그인 사용자 셀프 API 키 패널 — /developers 에 삽입. 발급/목록/폐기(본인 키만).
export default function MyApiKeys() {
  const [keys, setKeys] = useState<Key[]>([]);
  const [maxActive, setMaxActive] = useState(5);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [issued, setIssued] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    const res = await fetch("/api/my/api-keys");
    if (res.ok) { const j = await res.json(); setKeys(j.keys ?? []); setMaxActive(j.maxActive ?? 5); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  async function issue(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setErr(null); setIssued(null); setCopied(false);
    try {
      const res = await fetch("/api/my/api-keys", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ name: name.trim() }) });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) { setErr(j.error ?? "발급에 실패했습니다."); return; }
      setIssued(j.key as string); setName(""); await load();
    } finally { setBusy(false); }
  }

  async function revoke(id: number, active: boolean) {
    if (active && !confirm("이 키를 폐기하시겠어요? 즉시 사용이 중단됩니다.")) return;
    await fetch(`/api/my/api-keys/${id}`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ active: !active }) });
    await load();
  }

  const activeCount = keys.filter((k) => k.active).length;
  const btn: React.CSSProperties = { fontSize: 13, fontWeight: 700, borderRadius: 8, padding: "8px 14px", border: "none", cursor: "pointer" };
  const input: React.CSSProperties = { fontSize: 14, padding: "8px 10px", borderRadius: 8, border: `1px solid ${c.line}`, background: c.bg, color: c.ink };

  return (
    <div style={{ background: c.surfaceAlt, border: `1px solid ${c.line}`, borderRadius: 12, padding: "18px 20px" }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
        <strong style={{ fontSize: 15, color: c.ink }}>내 API 키</strong>
        <span style={{ fontSize: 12.5, color: c.muted }}>활성 {activeCount} / {maxActive}</span>
      </div>

      <form onSubmit={issue} style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
        <input value={name} onChange={(e) => setName(e.target.value)} required placeholder="키 이름 (예: 내 연동 테스트)" style={{ ...input, flex: 1, minWidth: 180 }} />
        <button type="submit" disabled={busy || !name.trim()} style={{ ...btn, background: c.action, color: "#fff", opacity: busy || !name.trim() ? 0.5 : 1 }}>
          {busy ? "발급 중…" : "새 키 발급"}
        </button>
      </form>
      {err && <p style={{ fontSize: 13, color: "#ba1a1a", margin: "8px 0 0" }}>{err}</p>}

      {issued && (
        <div style={{ marginTop: 12, border: "1px solid #F0C38A", background: "#FCEEE2", borderRadius: 10, padding: 14 }}>
          <p style={{ fontSize: 13, fontWeight: 700, color: "#93400F", margin: 0 }}>⚠️ 이 키는 지금 한 번만 표시됩니다. 복사해 안전하게 보관하세요.</p>
          <div style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "center" }}>
            <code style={{ flex: 1, wordBreak: "break-all", background: c.bg, border: "1px solid #F0C38A", borderRadius: 8, padding: "8px 10px", fontSize: 13, fontFamily: "ui-monospace, Menlo, monospace", color: c.ink }}>{issued}</code>
            <button type="button" onClick={() => navigator.clipboard?.writeText(issued).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); })} style={{ ...btn, background: "#93400F", color: "#fff", whiteSpace: "nowrap" }}>{copied ? "복사됨" : "복사"}</button>
          </div>
        </div>
      )}

      <div style={{ marginTop: 14 }}>
        {loading ? (
          <p style={{ fontSize: 13, color: c.muted, margin: 0 }}>불러오는 중…</p>
        ) : keys.length === 0 ? (
          <p style={{ fontSize: 13, color: c.muted, margin: 0 }}>발급된 키가 없습니다. 위에서 새 키를 발급하세요.</p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {keys.map((k) => (
              <div key={k.id} style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", background: c.bg, border: `1px solid ${c.line}`, borderRadius: 8, padding: "10px 12px" }}>
                <span style={{ fontSize: 13.5, fontWeight: 600, color: c.ink }}>{k.name}</span>
                <code style={{ fontSize: 12, color: c.muted, fontFamily: "ui-monospace, Menlo, monospace" }}>{k.keyPrefix}…</code>
                <span style={{ fontSize: 11.5, fontWeight: 700, padding: "2px 7px", borderRadius: 5, background: k.active ? "#E7F6EC" : "#eee", color: k.active ? "#1C7A3E" : "#888" }}>{k.active ? "활성" : "폐기"}</span>
                <span style={{ fontSize: 12, color: c.muted, marginLeft: "auto" }}>{k.lastUsedAt ? `최근 ${k.lastUsedAt.slice(0, 10)}` : "미사용"}</span>
                <button onClick={() => revoke(k.id, k.active)} style={{ ...btn, fontSize: 12, padding: "5px 10px", background: k.active ? "#FBE9E9" : "#E7F6EC", color: k.active ? "#ba1a1a" : "#1C7A3E" }}>{k.active ? "폐기" : "활성화"}</button>
              </div>
            ))}
          </div>
        )}
      </div>
      <p style={{ fontSize: 12, color: c.muted, margin: "12px 0 0" }}>기본 한도 60회/분. 이 키로 REST(<code>/api/v1</code>)와 MCP(<code>/api/mcp</code>) 모두 사용합니다.</p>
    </div>
  );
}
